import { dateLocale } from '@/utils/format';

/**
 * Timezone-explicit calendar arithmetic.
 *
 * ## Why this module exists
 *
 * `routes/calendar/datetime.ts` does everything in the browser's ambient zone
 * via `Date#getHours()` / `Date#setHours()`. That is correct-looking and wrong
 * in two places that matter:
 *
 * 1. **DST days are not 1440 minutes long.** A time grid that positions a block
 *    at `minutesSinceMidnight(start) / 1440` slides every block after a
 *    transition by an hour, and on a fall-back day two distinct instants
 *    (01:30 EDT and 01:30 EST) collapse onto the same pixel row. Positions here
 *    are `elapsedRealMinutesSinceLocalMidnight / actualMinutesInThatLocalDay`,
 *    which is right on 23-, 24- and 25-hour days and keeps the two 01:30s apart
 *    because they are genuinely an hour apart in real time.
 * 2. **The ambient zone is untestable.** Nothing can assert DST behaviour when
 *    the only zone available is whatever the CI box happens to be set to.
 *    Every function here takes the zone as an argument, so a test can render a
 *    New York fall-back day on a UTC machine.
 *
 * ## The two representations
 *
 * - An **instant** is a `Date`. It is a point on the real timeline and carries
 *   no zone.
 * - A **day key** is `"YYYY-MM-DD"`: one calendar day *in a named zone*. Days
 *   are keys rather than `Date`s on purpose — a `Date` pinned to "midnight"
 *   silently means midnight *somewhere*, and that somewhere is where the bugs
 *   come from. Arithmetic on keys goes through `Date.UTC`, which is a pure
 *   proleptic Gregorian calendar with no transitions to trip over.
 *
 * Everything is dependency-free and uses `Intl` for the zone database, which
 * every browser and Node build we target already ships.
 */

/** A calendar day in some zone, `YYYY-MM-DD`. */
export type DayKey = string;

/** Wall-clock fields of an instant as seen from a zone. */
export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  second: number;
}

export const MINUTES_PER_HOUR = 60;
/** The *nominal* length of a day. Real days are 23, 24 or 25 hours long. */
export const NOMINAL_MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** The viewer's own zone — what everything renders in unless told otherwise. */
export function localZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(zone: string): Intl.DateTimeFormat {
  const existing = partsCache.get(zone);
  if (existing) return existing;
  // `en-US` with `hourCycle: 'h23'` so `formatToParts` never yields "24" for
  // midnight, which is what `hour12: false` does in several engines.
  const created = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  partsCache.set(zone, created);
  return created;
}

/** Decompose an instant into the wall-clock fields a viewer in `zone` sees. */
export function zonedParts(instant: Date, zone: string): ZonedParts {
  const found: Record<string, number> = {};
  for (const part of partsFormatter(zone).formatToParts(instant)) {
    if (part.type === 'literal') continue;
    const value = Number(part.value);
    if (!Number.isNaN(value)) found[part.type] = value;
  }
  return {
    year: found.year ?? 1970,
    month: found.month ?? 1,
    day: found.day ?? 1,
    hour: found.hour ?? 0,
    minute: found.minute ?? 0,
    second: found.second ?? 0,
  };
}

/**
 * The zone's UTC offset **at that instant**, in minutes east of UTC.
 * `-240` for New York in July, `-300` in January.
 */
export function offsetMinutesAt(instant: Date, zone: string): number {
  const parts = zonedParts(instant, zone);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  // `parts` is truncated to whole seconds, so the instant's own milliseconds
  // have to be added back before dividing or a sub-second instant rounds wrong.
  return Math.round((asIfUtc - instant.getTime() + instant.getMilliseconds()) / 60_000);
}

/* --------------------------------------------------------------- day keys */

const KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function dayKeyOf(year: number, month: number, day: number): DayKey {
  return `${`${year}`.padStart(4, '0')}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
}

/** `{year, month, day}` of a key. Malformed keys degrade to the epoch. */
export function partsOfDayKey(key: DayKey): { year: number; month: number; day: number } {
  const match = KEY_PATTERN.exec(key);
  if (!match) return { year: 1970, month: 1, day: 1 };
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** Which calendar day an instant falls on, as seen from `zone`. */
export function dayKeyInZone(instant: Date, zone: string): DayKey {
  const parts = zonedParts(instant, zone);
  return dayKeyOf(parts.year, parts.month, parts.day);
}

/** Calendar arithmetic in UTC, where every day is exactly 24 hours. */
export function addDayKey(key: DayKey, amount: number): DayKey {
  const { year, month, day } = partsOfDayKey(key);
  const moved = new Date(Date.UTC(year, month - 1, day + amount));
  return dayKeyOf(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate());
}

/** Month arithmetic, clamping so 31 Jan + 1 month is 28/29 Feb rather than 3 Mar. */
export function addMonthKey(key: DayKey, amount: number): DayKey {
  const { year, month, day } = partsOfDayKey(key);
  const target = new Date(Date.UTC(year, month - 1 + amount, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  return dayKeyOf(target.getUTCFullYear(), target.getUTCMonth() + 1, Math.min(day, lastDay));
}

/** Day of week, 0 = Sunday. Independent of any zone: a key names a date. */
export function weekdayOfDayKey(key: DayKey): number {
  const { year, month, day } = partsOfDayKey(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

export function startOfMonthKey(key: DayKey): DayKey {
  const { year, month } = partsOfDayKey(key);
  return dayKeyOf(year, month, 1);
}

/** `weekStartsOn` is 0 (Sunday) … 6 (Saturday). */
export function startOfWeekKey(key: DayKey, weekStartsOn = 0): DayKey {
  const diff = (weekdayOfDayKey(key) - weekStartsOn + 7) % 7;
  return addDayKey(key, -diff);
}

/** `count` consecutive day keys beginning at `start`. */
export function dayKeyRange(start: DayKey, count: number): DayKey[] {
  const out: DayKey[] = [];
  for (let index = 0; index < count; index += 1) out.push(addDayKey(start, index));
  return out;
}

export function isSameMonthKey(a: DayKey, b: DayKey): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

export function isWeekendKey(key: DayKey): boolean {
  const weekday = weekdayOfDayKey(key);
  return weekday === 0 || weekday === 6;
}

export function todayKey(zone: string, now: Date = new Date()): DayKey {
  return dayKeyInZone(now, zone);
}

/* ---------------------------------------------------- wall time ↔ instant */

/**
 * The instant at which `zone`'s wall clock reads `key` + `minutesIntoDay`.
 *
 * Two passes: guess with the offset that applies at the naive UTC reading,
 * then re-resolve with the offset that actually applies at the guessed
 * instant. That converges for every real transition (offsets change by at most
 * a couple of hours, transitions are hours apart).
 *
 * **Ambiguity is real and is resolved, not hidden.** On a fall-back day 01:30
 * happens twice; this returns the *first* (the pre-transition, still-DST) one,
 * which is what every major calendar does. On a spring-forward day 02:30 never
 * happens; this returns the instant one hour later on the wall clock (03:30),
 * which is the RFC 5545 §3.3.5 recommendation.
 */
export function instantFromWall(key: DayKey, minutesIntoDay: number, zone: string): Date {
  const { year, month, day } = partsOfDayKey(key);
  const naive = Date.UTC(year, month - 1, day) + minutesIntoDay * 60_000;

  const firstOffset = offsetMinutesAt(new Date(naive), zone);
  const firstGuess = naive - firstOffset * 60_000;
  const secondOffset = offsetMinutesAt(new Date(firstGuess), zone);
  if (secondOffset === firstOffset) return new Date(firstGuess);

  const secondGuess = naive - secondOffset * 60_000;
  // Prefer the earlier candidate that actually round-trips to the requested
  // wall time; if neither does (a gap), keep the later one — the wall clock
  // skipped over the request, so "one hour on" is the honest answer.
  const roundTrips = (candidate: number): boolean =>
    offsetMinutesAt(new Date(candidate), zone) === (naive - candidate) / 60_000;
  if (roundTrips(firstGuess)) return new Date(firstGuess);
  if (roundTrips(secondGuess)) return new Date(secondGuess);
  return new Date(Math.max(firstGuess, secondGuess));
}

/** Midnight at the start of `key` in `zone`, as an instant. */
export function startOfZonedDay(key: DayKey, zone: string): Date {
  return instantFromWall(key, 0, zone);
}

/**
 * How many real minutes that calendar day lasts in that zone: 1380 on a
 * spring-forward day, 1440 normally, 1500 on a fall-back day.
 */
export function minutesInZonedDay(key: DayKey, zone: string): number {
  const start = startOfZonedDay(key, zone).getTime();
  const next = startOfZonedDay(addDayKey(key, 1), zone).getTime();
  return Math.round((next - start) / 60_000);
}

/**
 * Real minutes elapsed from that day's local midnight to `instant`.
 * Negative before the day, greater than the day's length after it — callers
 * clamp, because an event can start yesterday and end tomorrow.
 */
export function minutesFromDayStart(instant: Date, key: DayKey, zone: string): number {
  return (instant.getTime() - startOfZonedDay(key, zone).getTime()) / 60_000;
}

/** Does `[start, end)` intersect the calendar day `key` in `zone`? */
export function overlapsDayKey(start: Date, end: Date, key: DayKey, zone: string): boolean {
  const dayStart = startOfZonedDay(key, zone).getTime();
  const dayEnd = startOfZonedDay(addDayKey(key, 1), zone).getTime();
  // A zero-length event is still *on* its day, so an exact-midnight point event
  // is not swallowed by the half-open interval.
  const from = start.getTime();
  const to = Math.max(end.getTime(), from + 1);
  return from < dayEnd && to > dayStart;
}

/* ------------------------------------------------------------- formatting */

const formatCache = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = dateLocale();
  const key = `${locale ?? ''}|${zone}|${JSON.stringify(options)}`;
  const existing = formatCache.get(key);
  if (existing) return existing;
  // The locale is Settings → General's date format (`undefined` = the
  // viewer's own); the zone is the one the calendar is showing.
  const created = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
  formatCache.set(key, created);
  return created;
}

export const fmtTimeIn = (instant: Date, zone: string): string =>
  formatter(zone, { hour: 'numeric', minute: '2-digit' }).format(instant);

export const fmtHourIn = (instant: Date, zone: string): string =>
  formatter(zone, { hour: 'numeric' }).format(instant);

export const fmtWeekdayShortOf = (key: DayKey, zone: string): string =>
  formatter(zone, { weekday: 'short' }).format(startOfZonedDay(key, zone));

export const fmtWeekdayLongOf = (key: DayKey, zone: string): string =>
  formatter(zone, { weekday: 'long' }).format(startOfZonedDay(key, zone));

export const fmtDayNumberOf = (key: DayKey): string => String(partsOfDayKey(key).day);

export const fmtMonthYearOf = (key: DayKey, zone: string): string =>
  formatter(zone, { month: 'long', year: 'numeric' }).format(startOfZonedDay(key, zone));

export const fmtMonthDayOf = (key: DayKey, zone: string): string =>
  formatter(zone, { month: 'short', day: 'numeric' }).format(startOfZonedDay(key, zone));

export const fmtFullDateOf = (key: DayKey, zone: string): string =>
  formatter(zone, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).format(
    startOfZonedDay(key, zone),
  );

/** `"GMT-4"` / `"EDT"`-style label, used to disclose a foreign event zone. */
export function zoneLabel(instant: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'short',
  }).formatToParts(instant);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? zone;
}

/**
 * The disclosure line doc 06 §7 asks for when an event was authored somewhere
 * else: `"2:00 PM – 3:00 PM (Europe/London)"`. Returns `undefined` when the
 * event's zone and the viewer's agree, so callers can render nothing.
 *
 * It compares *offsets at that instant*, not zone names: `Europe/London` and
 * `Europe/Dublin` are the same clock, and telling someone in London that a
 * London event is "in another timezone" is noise.
 */
export function foreignZoneNote(
  instant: Date,
  eventZone: string | undefined,
  viewerZone: string,
): string | undefined {
  if (!eventZone || eventZone === viewerZone) return undefined;
  try {
    if (offsetMinutesAt(instant, eventZone) === offsetMinutesAt(instant, viewerZone)) {
      return undefined;
    }
  } catch {
    // An unknown TZID from a foreign CalDAV server: say the zone, do not throw.
    return eventZone;
  }
  return eventZone;
}
